import { Box, Collapse, Stack, SvgIcon, Typography, keyframes } from '@mui/material'
import { PropTypes } from 'prop-types'
import React, { useEffect, useState } from 'react'
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import StarIcon from '@mui/icons-material/Star';
import { Whatshot } from '@mui/icons-material';
import { readableInk } from 'src/utils/colour';
import CustomCareerBar from './CustomCareerBar';
import { ReactComponent as flagIcon } from '../../assets/images/flag.svg'


const CustomBar = ({ isWidget, onWidgetCareerClick, data, maxLength, addToCompare, compareCareersState, showCareerDetailsPopup, index }) => {
    const minLength = 3
    // Widget: the sectors a visitor has opened are remembered for the visit (per course), so they are still open when the visitor
    // comes back from a career's details, another tab or a reload.
    const openKey = isWidget ? `tsw-open:${window.location.pathname}:${new URLSearchParams(window.location.search).get('url') || ''}` : null
    const readOpen = () => {
        try { return JSON.parse(window.sessionStorage.getItem(openKey) || '[]') } catch (error) { return [] }
    }
    const [checked, setChecked] = useState(() => Boolean(isWidget && data?.name && readOpen().includes(data.name)))
    const toggleOpen = () => {
        const next = !checked
        setChecked(next)
        if (!isWidget || !data?.name) return
        try {
            const names = new Set(readOpen())
            if (next) names.add(data.name); else names.delete(data.name)
            window.sessionStorage.setItem(openKey, JSON.stringify([...names]))
        } catch (error) {
            // storage can be blocked inside a frame; the sector then simply is not remembered
        }
    }
    const [careers, setCareers] = useState([])
    const [styleData, setStyleData] = useState({
        barColor: '#fff',
        barWidth: 0
    })
    const minWidth = 550
    const arbitarWidth = 330 / maxLength
    const { barColor, barWidth } = styleData
    useEffect(() => {
        if (data) {
            setCareers(data?.careers)
            setStyleData(prevData => ({
                ...prevData,
                barColor: data?.color,
                // barWidth:  data?.careers?.length ? (minWidth + data.careers.length * arbitarWidth) : 100 - len/max
                // eslint-disable-next-line no-unsafe-optional-chaining
                barWidth: data?.careers?.length ? `${(100 - Math.min((maxLength / data?.careers?.length === 1 ? maxLength / data?.careers?.length : maxLength / data?.careers?.length * 2), 40))}%` : 10
                // barWidth:  '60%'

            }))
        }
    }, [data, arbitarWidth, maxLength])

    const graphWidth = keyframes`from {
    width: 30%
    }
`;

    // In the widget an opened sector is drawn as one group: a soft tint of the sector colour behind the solid header and the
    // outlined career rows, so the rows read as part of the header above them.
    const Group = isWidget ? Box : React.Fragment
    const groupProps = isWidget ? {
        sx: {
            width: { xs: '100%', sm: barWidth },
            mb: '3px',
            p: checked ? '3px' : 0,
            borderRadius: '16px',
            transition: 'background-color .2s, padding .2s',
            backgroundColor: checked ? `${barColor}24` : 'transparent',
            '@supports (color: color-mix(in srgb, red, blue))': {
                backgroundColor: checked ? `color-mix(in srgb, ${barColor} 14%, #fff)` : 'transparent',
            },
        },
    } : {}

    return (
        <Group {...groupProps}>
            {data?.careers?.length > 0 &&
                <Box
                    className='bar'
                    sx={(theme) => ({
                        transition: theme.transitions.create(["background", "background-color"], {
                            duration: theme.transitions.duration.shorter,
                        }),
                        backgroundColor: barColor,
                        // widget: the group takes the width and the spacing
                        // widget: dark text on a light sector colour (the yellow), white on the rest
                        ...(isWidget ? { margin: '0 !important', color: `${readableInk(barColor)} !important` } : {}),
                        width: isWidget ? '100%' : { xs: '100%', sm: barWidth },
                        animation: `${graphWidth} ${Math.min(0.6 + index / 20, 1)}s ease`
                    })}
                    onClick={toggleOpen}
                >
                    <Box display="flex" justifyContent='space-between' pl={2} alignItems='center'>
                        <Stack direction='row' alignItems='center' gap={1}>
                           { data?.isEmerging && <Whatshot />}
                           { data?.priority && <StarIcon />}
                            <Typography sx={{  textAlign: 'start' }} variant='capitalize'>
                                {data?.name}
                                {/* {data?.sector_name} */}
                            </Typography>
                        </Stack>
                        {data?.careers.some(career => career?.careerType === 'CAREER_GOAL') &&
                            <Box sx={{ mx: 1 }}>
                                <SvgIcon
                                    component={flagIcon}
                                    inheritViewBox
                                />
                            </Box>}
                        <Box flex={1} />
                        {isWidget ? (
                            // widget: the count is a small pill
                            <Typography
                                noWrap
                                variant='capitalize'
                                component='span'
                                sx={{ fontSize: '0.78rem !important', lineHeight: 1, px: 1.25, py: 0.6, borderRadius: '999px', backgroundColor: 'rgba(0,0,0,0.16)', '@supports (color: color-mix(in srgb, red, blue))': { backgroundColor: 'color-mix(in srgb, currentColor 16%, transparent)' } }}
                            >
                                {`${data?.careers?.length} ${data?.careers?.length === 1 ? 'career' : 'careers'}`}
                            </Typography>
                        ) : (
                            <Typography noWrap variant='capitalize'>
                                {`${data?.careers?.length} Careers`}
                            </Typography>
                        )}
                        {checked ?
                            <KeyboardArrowDownOutlinedIcon sx={{ mr: 1 }} fontSize='large' /> :
                            <ChevronRightIcon sx={{ mr: 1 }} fontSize='large' />}
                    </Box>
                </Box>}
            <Collapse
                in={checked}
            >
                <Stack
                    gap={isWidget ? 0.375 : 0.5}
                    sx={isWidget ? { pt: 0.375 } : undefined}
                >
                    {data?.careers?.map(career =>
                        <CustomCareerBar
                            isWidget={isWidget}
                            career={career}
                            barColor={barColor}
                            barWidth={barWidth}
                            addToCompare={addToCompare}
                            compareCareersState={compareCareersState}
                            showCareerDetailsPopup={showCareerDetailsPopup}
                            onWidgetCareerClick={onWidgetCareerClick}
                        />
                    )}
                </Stack>
            </Collapse>
        </Group>
    )
}

export default CustomBar

CustomBar.propTypes = {
    data: PropTypes.object,
    maxLength: PropTypes.number,
    addToCompare: PropTypes.func,
    onWidgetCareerClick: PropTypes.func,
    compareCareersState: PropTypes.array,
    showCareerDetailsPopup: PropTypes.func,
    index: PropTypes.number,
    isWidget: PropTypes.bool,
}